import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useGetMeQuery, useLogoutMutation } from '../store/auth-api.js';
import { BrandLogo, MenuToggle } from '../ui/BrandLogo.js';
import { IconChevronDown } from '../ui/icons.js';
import { useAppDispatch } from '../store/hooks.js';
import { pushToast } from '../ui/toast-store.js';
import { NotificationBell } from '../features/notifications/NotificationBell.js';
import { TestToolsCorner } from '../features/maintenance/TestToolsCorner.js';
import { ConnectionStatus } from './ConnectionStatus.js';
import { NAV_ITEMS, ROUTE_TITLES } from './nav.js';
import { ThemeSwitch } from './ThemeSwitch.js';
import { UserMenu } from './UserMenu.js';
import styles from './AppShell.module.css';

/** The product name, as the header shows it beside the logo. */
const APP_NAME = 'Voice Support Assistant';

/**
 * The persistent app shell (S03).
 *
 * The Avatar Assistant's frame: a full-height side menu headed by the logo, and
 * beside it a header — organisation and product on the left; connection,
 * notifications, theme and the user on the right — over the page. Rendered only for a signed-in user — the route
 * guard above it handles the redirect — so `me` is present by the time this
 * mounts and the identity block never flickers through an empty state.
 */
export function AppShell(): JSX.Element {
  const { data: me } = useGetMeQuery();
  const [logout, { isLoading: signingOut }] = useLogoutMutation();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  // Falls back to the section, so a nested route keeps its section's heading:
  // `/agents/<id>` is still Agent Configuration, and without this the page
  // heading would read the product name the moment an agent was opened.
  const section = `/${pathname.split('/')[1] ?? ''}`;
  const [title, subtitle] = ROUTE_TITLES[pathname] ?? ROUTE_TITLES[section] ?? [APP_NAME, ''];

  // Below 900px the sidebar leaves the flow and becomes a drawer. The state lives
  // here because the control that opens it and the panel it opens are siblings —
  // neither owns the other.
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const firstLink = useRef<HTMLAnchorElement>(null);

  // Navigating is the whole point of the drawer, so arriving somewhere closes
  // it. Keyed on pathname so it also closes when a link points at the current
  // route and nothing else changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    firstLink.current?.focus();

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);

    // Without this the page scrolls behind the scrim, which makes the drawer
    // feel like a floating panel rather than a layer over a held page.
    document.body.classList.add('menu-open');

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('menu-open');
      // The button that opened it is where focus belongs on the way out; the
      // link that had focus may no longer be on screen.
      toggleRef.current?.focus();
    };
  }, [menuOpen]);

  async function onSignOut(): Promise<void> {
    try {
      const result = await logout().unwrap();
      // Sign out of the IdP too when it supports it. Full navigation, since we
      // are leaving the SPA. With federated sign-out the IdP takes over. Locally
      // we land on our own login page, and say so there: the IdP session is
      // still open, which is why signing in again will not ask for anything.
      window.location.assign(
        result.endSessionUrl ?? (result.ssoStillActive ? '/login?signedOut=sso' : '/login'),
      );
    } catch {
      dispatch(pushToast('error', 'Could not sign out cleanly. Please close your browser.'));
    }
  }

  return (
    <div className={styles.layout}>
      {/* Its own stacking context (the backdrop blur), raised over the page and
          the phone drawer, so panels opened from it — notifications, the user
          menu — sit above everything they open over. Declared before the side
          menu so the toggle comes first in the tab order on a phone. */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          {/* Phones only: below 900px the side menu is a drawer, opened here. */}
          <button
            type="button"
            ref={toggleRef}
            className={styles.toggle}
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="app-nav"
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            title={menuOpen ? 'Close navigation' : 'Open navigation'}
          >
            <MenuToggle open={menuOpen} />
          </button>

          {/* Phones only. The side menu, and the logo heading it, are off screen
              there until opened, so the logo takes the breadcrumb's place. */}
          <NavLink
            to="/"
            className={`${styles.mobileBrand}`}
            aria-label={`${APP_NAME} — Dashboard`}
          >
            <BrandLogo size="sidebar" />
          </NavLink>

          {/* The organisation, then the product — the Avatar Assistant's
              breadcrumb. The chevron is its look and nothing more: as there, the
              label opens no menu, since there is only one product to be in. */}
          <span className={styles.orgAvatar} aria-hidden="true">
            S
          </span>
          <span className={styles.orgName}>Sirma AI</span>
          <span className={styles.slash} aria-hidden="true">
            /
          </span>
          <span className={styles.appName}>
            <span className={styles.appNameText}>{APP_NAME}</span>
            <IconChevronDown aria-hidden="true" />
          </span>
        </div>

        <div className={styles.headerRight}>
          <ConnectionStatus />
          <span className={styles.vsep} aria-hidden="true" />
          <NotificationBell />
          <ThemeSwitch className={styles.themeSwitch} />
          <span className={styles.vsep} aria-hidden="true" />
          <UserMenu me={me} onSignOut={() => void onSignOut()} signingOut={signingOut} />
        </div>
      </header>

      {/* Only rendered while open, and display:none above 900px, so the desktop
          layout can never end up with an invisible click-blocker over it. */}
      {menuOpen && (
        <div className={styles.scrim} onClick={() => setMenuOpen(false)} aria-hidden="true" />
      )}

      <aside id="app-nav" className={`${styles.sidebar} ${menuOpen ? styles.sidebarOpen : ''}`}>
        <NavLink to="/" className={`${styles.brand}`} aria-label={`${APP_NAME} — Dashboard`}>
          <BrandLogo />
        </NavLink>
        <nav aria-label="Main" className={styles.nav}>
          {NAV_ITEMS.map((item, index) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              ref={index === 0 ? firstLink : undefined}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
            >
              <item.Icon />
              <span className={styles.navText}>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className={styles.dots} aria-hidden="true" />
      </aside>

      {/* Scrolls inside the container, never the page — S03's requirement that
          wide tables scroll within their own container. */}
      <main className={styles.content}>
        <div className={styles.pageHeader}>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <Outlet />
      </main>

      {/* Test environments only. The server decides: where the tools are off,
          `testTools` is false and there is nothing in the corner to find. */}
      {me?.testTools === true && <TestToolsCorner />}
    </div>
  );
}
